/** RFC 0319 3b: what a task touches, as one list of refs. */

import type { Task, TaskRef } from './types'

/** A plain ref with only defined fields, because Automerge refuses `undefined`. */
export function cleanRef(ref: TaskRef): TaskRef | null {
  const type = typeof ref?.type === 'string' ? ref.type.trim() : ''
  const target = typeof ref?.target === 'string' ? ref.target.trim() : ''
  if (!type || !target) return null
  const role = typeof ref.role === 'string' ? ref.role.trim() : ''
  return role ? { type, target, role } : { type, target }
}

export function sameRef(a: TaskRef, b: TaskRef): boolean {
  return (
    a.type === b.type &&
    a.target === b.target &&
    (a.role ?? '') === (b.role ?? '')
  )
}

/** `refs` followed by each of `more` not already in it. */
export function mergeRefs(refs: TaskRef[], more: TaskRef[]): TaskRef[] {
  const out = [...refs]
  for (const raw of more) {
    const ref = cleanRef(raw)
    if (ref && !out.some((r) => sameRef(r, ref))) out.push(ref)
  }
  return out
}

/** A task's refs, normalized and deduplicated. */
export function taskRefs(task: Partial<Task>): TaskRef[] {
  return Array.isArray(task.refs) ? mergeRefs([], task.refs) : []
}

/** The session whose log holds the task's object entry: the one that made it. */
export function ownerSession(task: Partial<Task>): string | undefined {
  const refs = taskRefs(task)
  return (
    refs.find((r) => r.type === 'session' && r.role === 'created-by')?.target ??
    refs.find((r) => r.type === 'session')?.target
  )
}

/** True when `refs` names `target`, whatever the kind or role. */
export function namesTarget(refs: TaskRef[], target: string): boolean {
  return refs.some((r) => r.target === target)
}
